<template>
    <div class="space-y-10">
        <!-- Sending Domain Section -->
        <section>
            <h2 class="text-lg font-medium text-iron-700">
                {{ $t("event_types.event_detail.domains.sending_title") }}
            </h2>
            <p class="text-sm text-iron-500">
                {{ $t("event_types.event_detail.domains.sending_description") }}
            </p>

            <div v-if="emailDomains.length > 0" class="mt-6 space-y-2">
                <button
                    v-for="domain in emailDomains"
                    :key="domain.id"
                    type="button"
                    class="group rounded-xl bg-white p-4 w-full border text-left transition-all duration-200"
                    :class="[
                        selectedDomain?.id === domain.id
                            ? 'border-z-primary-500 ring-z-primary-100 shadow-sm ring-2'
                            : 'border-iron-200 hover:border-iron-300 hover:shadow-sm',
                        !domain.can_be_used_for_emails &&
                            'cursor-not-allowed opacity-50',
                    ]"
                    :disabled="!domain.can_be_used_for_emails"
                    @click="selectDomain(domain)"
                >
                    <div class="flex items-center justify-between">
                        <div class="gap-3 flex items-center">
                            <div
                                class="h-9 w-9 rounded-lg flex items-center justify-center transition-colors"
                                :class="[
                                    selectedDomain?.id === domain.id
                                        ? 'bg-z-primary-100 text-z-primary-600'
                                        : 'bg-iron-100 text-iron-500 group-hover:bg-iron-200',
                                ]"
                            >
                                <AtSign class="h-4 w-4" />
                            </div>
                            <span
                                class="font-medium"
                                :class="[
                                    selectedDomain?.id === domain.id
                                        ? 'text-iron-900'
                                        : 'text-iron-700',
                                ]"
                            >
                                {{ domain.name }}
                            </span>
                        </div>
                        <div
                            class="h-5 w-5 flex items-center justify-center rounded-full border-2 transition-all"
                            :class="[
                                selectedDomain?.id === domain.id
                                    ? 'border-z-primary-500 bg-z-primary-500'
                                    : 'border-iron-300 group-hover:border-iron-400',
                            ]"
                        >
                            <Check
                                v-if="selectedDomain?.id === domain.id"
                                class="h-3 w-3 text-white"
                                :stroke-width="3"
                            />
                        </div>
                    </div>
                </button>

                <!-- Email Username Input -->
                <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 -translate-y-2"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-2"
                >
                    <div
                        v-if="selectedDomain"
                        class="mt-4 rounded-xl border-iron-200 bg-iron-50/50 p-4 border"
                    >
                        <h3 class="text-sm font-medium text-iron-700">
                            {{
                                $t(
                                    "event_types.event_detail.domains.default_sender_title",
                                )
                            }}
                        </h3>
                        <p class="mb-3 mt-1 text-xs text-iron-500">
                            {{
                                $t(
                                    "event_types.event_detail.domains.default_sender_hint",
                                )
                            }}
                        </p>
                        <Label
                            for="email-username"
                            class="text-sm font-medium text-iron-700"
                        >
                            {{ $t("domains.email_username") }}
                        </Label>
                        <div class="mt-2 flex items-center">
                            <Input
                                id="email-username"
                                v-model="emailUsername"
                                class="rounded-r-none border-r-0 focus:z-10"
                                placeholder="booking"
                            />
                            <div
                                class="h-9 rounded-r-md border-iron-200 bg-iron-100 px-3 text-sm text-iron-600 flex items-center border"
                            >
                                @{{ selectedDomain.name }}
                            </div>
                        </div>

                        <Label
                            for="sender-name"
                            class="mt-4 text-sm font-medium text-iron-700"
                        >
                            {{ $t("sender_name") }}
                        </Label>
                        <Input
                            id="sender-name"
                            v-model="senderName"
                            class="mt-2"
                            :placeholder="
                                ownerName || $t('sender_name_placeholder')
                            "
                        />
                        <p class="mt-1.5 text-xs text-iron-500">
                            {{ $t("sender_name_hint") }}
                        </p>

                        <Label
                            for="reply-to"
                            class="mt-4 text-sm font-medium text-iron-700"
                        >
                            {{ $t("reply_to") }}
                        </Label>
                        <Input
                            id="reply-to"
                            v-model="replyTo"
                            type="email"
                            class="mt-2"
                            :placeholder="
                                emailUsername
                                    ? `${emailUsername}@${selectedDomain.name}`
                                    : $t('reply_to_placeholder')
                            "
                        />
                        <p class="mt-1.5 text-xs text-iron-500">
                            {{ $t("reply_to_hint") }}
                        </p>
                    </div>
                </Transition>

                <!-- Per-host senders (round-robin teams) -->
                <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 -translate-y-2"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-2"
                >
                    <div
                        v-if="selectedDomain && hostSenders.length >= 1"
                        class="mt-4 rounded-xl border-iron-200 bg-iron-50/50 p-4 border"
                    >
                        <h3 class="text-sm font-medium text-iron-700">
                            {{
                                $t(
                                    "event_types.event_detail.domains.host_senders_title",
                                )
                            }}
                        </h3>
                        <p class="mt-1 text-xs text-iron-500">
                            {{
                                $t(
                                    "event_types.event_detail.domains.host_senders_description",
                                )
                            }}
                        </p>

                        <div class="mt-4 space-y-5">
                            <div
                                v-for="host in hostSenders"
                                :key="host.userId"
                                class="border-iron-200/70 not-first:pt-5 not-first:border-t"
                            >
                                <p class="text-sm font-medium text-iron-700">
                                    {{ host.name }}
                                </p>
                                <div class="mt-2 flex items-center">
                                    <Input
                                        v-model="host.emailUsername"
                                        class="rounded-r-none border-r-0 focus:z-10"
                                        :placeholder="
                                            emailUsername || 'booking'
                                        "
                                    />
                                    <div
                                        class="h-9 rounded-r-md border-iron-200 bg-iron-100 px-3 text-sm text-iron-600 flex items-center border"
                                    >
                                        @{{ selectedDomain.name }}
                                    </div>
                                </div>
                                <Input
                                    v-model="host.senderName"
                                    class="mt-2"
                                    :placeholder="
                                        host.name ||
                                        $t('sender_name_placeholder')
                                    "
                                />
                                <Input
                                    v-model="host.replyTo"
                                    type="email"
                                    class="mt-2"
                                    :placeholder="
                                        host.emailUsername || emailUsername
                                            ? `${host.emailUsername || emailUsername}@${selectedDomain.name}`
                                            : $t('reply_to_placeholder')
                                    "
                                />
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>

            <!-- Empty State for Sending Domains -->
            <Link v-else href="/domains" class="mt-6 block">
                <PromoBanner :is-paid="true">
                    <template #icon>
                        <Mail class="h-5 w-5 text-[#E92F34]" />
                    </template>
                    <template #title>
                        {{
                            $t("event_types.event_detail.domains.banner.title")
                        }}
                    </template>
                    <template #description>
                        {{
                            $t(
                                "event_types.event_detail.domains.banner.description",
                            )
                        }}
                    </template>
                </PromoBanner>
            </Link>
        </section>

        <!-- Divider -->
        <div class="border-iron-200 border-t"></div>

        <!-- Booking Page Domain Section -->
        <section>
            <h2 class="text-lg font-medium text-iron-700">
                {{ $t("event_types.event_detail.domains.page_title") }}
            </h2>
            <p class="text-sm text-iron-500">
                {{ $t("event_types.event_detail.domains.page_description") }}
            </p>

            <div v-if="pageDomains.length > 0" class="mt-6 space-y-2">
                <button
                    v-for="pageDomain in pageDomainOptions"
                    :key="pageDomain.id ?? 'zaptime'"
                    type="button"
                    class="group rounded-xl bg-white p-4 w-full border text-left transition-all duration-200"
                    :class="[
                        selectedPageDomainId === pageDomain.id
                            ? 'border-z-primary-500 ring-z-primary-100 shadow-sm ring-2'
                            : 'border-iron-200 hover:border-iron-300 hover:shadow-sm',
                    ]"
                    @click="selectPageDomain(pageDomain)"
                >
                    <div class="flex items-center justify-between">
                        <div class="gap-3 flex items-center">
                            <div
                                class="h-9 w-9 rounded-lg flex items-center justify-center transition-colors"
                                :class="[
                                    selectedPageDomainId === pageDomain.id
                                        ? 'bg-z-primary-100 text-z-primary-600'
                                        : 'bg-iron-100 text-iron-500 group-hover:bg-iron-200',
                                ]"
                            >
                                <Link2 class="h-4 w-4" />
                            </div>
                            <div>
                                <span
                                    class="font-medium"
                                    :class="[
                                        selectedPageDomainId === pageDomain.id
                                            ? 'text-iron-900'
                                            : 'text-iron-700',
                                    ]"
                                >
                                    {{ pageDomain.domain }}
                                </span>
                                <span
                                    v-if="
                                        pageDomain.is_default &&
                                        pageDomain.team_id === eventType.team_id
                                    "
                                    class="ml-2 bg-iron-100 px-2 py-0.5 text-xs font-medium text-iron-600 inline-flex items-center rounded-full"
                                >
                                    {{ $t("default") }}
                                </span>
                            </div>
                        </div>
                        <div
                            class="h-5 w-5 flex items-center justify-center rounded-full border-2 transition-all"
                            :class="[
                                selectedPageDomainId === pageDomain.id
                                    ? 'border-z-primary-500 bg-z-primary-500'
                                    : 'border-iron-300 group-hover:border-iron-400',
                            ]"
                        >
                            <Check
                                v-if="selectedPageDomainId === pageDomain.id"
                                class="h-3 w-3 text-white"
                                :stroke-width="3"
                            />
                        </div>
                    </div>
                </button>
            </div>

            <!-- Empty State for Page Domains -->
            <Link v-else href="/page-domains" class="mt-6 block">
                <PromoBanner :is-paid="true">
                    <template #icon>
                        <Globe class="h-5 w-5 text-[#E92F34]" />
                    </template>
                    <template #title>
                        {{
                            $t(
                                "event_types.event_detail.domains.page_banner.title",
                            )
                        }}
                    </template>
                    <template #description>
                        {{
                            $t(
                                "event_types.event_detail.domains.page_banner.description",
                            )
                        }}
                    </template>
                </PromoBanner>
            </Link>
        </section>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import route from "ziggy-js";
import { router, Link } from "@inertiajs/vue3";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { watchThrottled } from "@vueuse/core";
import { Mail, Globe, AtSign, Link2, Check } from "lucide-vue-next";
import PromoBanner from "../../Atomic/PromoBanner.vue";
import type { EventType } from "../types/EventType";
import {
    getSelectedPageDomainId,
    getTeamDefaultPageDomain,
} from "../utils/getSelectedPageDomainId";

export type PageDomain = {
    id: number;
    domain: string;
    is_default: boolean;
    team_id: number;
};

type PageDomainOption = Omit<PageDomain, "id"> & { id: number | null };

export type EmailDomain = {
    id: number;
    name: string;
    can_be_used_for_emails: boolean;
};

type HostSender = {
    userId: number;
    name: string;
    emailUsername: string;
    senderName: string;
    replyTo: string;
};

const selectedDomain = ref<EmailDomain | null>(null);
const emailUsername = ref("zaptime");
const senderName = ref("");
const replyTo = ref("");
const hostSenders = ref<HostSender[]>([]);

const props = withDefaults(
    defineProps<{
        eventType: EventType;
        pageDomains?: PageDomain[];
        emailDomains?: EmailDomain[];
        teamUsers?: any[];
    }>(),
    {
        pageDomains: () => [],
        emailDomains: () => [],
        teamUsers: () => [],
    },
);

const emit = defineEmits(["on-finish"]);

const ownerName = computed(
    () =>
        props.teamUsers.find((u: any) => u.id === props.eventType.user_id)
            ?.name ?? "",
);

const selectedPageDomainId = computed(() =>
    getSelectedPageDomainId(
        props.eventType.page_domain_id,
        props.pageDomains,
        props.eventType.team_id,
    ),
);

// Without a default domain, an unassigned event type lives on zapti.me, so offer
// it as an explicit choice that can also be switched back to.
const pageDomainOptions = computed((): PageDomainOption[] => {
    if (getTeamDefaultPageDomain(props.pageDomains, props.eventType.team_id)) {
        return props.pageDomains;
    }

    return [
        {
            id: null,
            domain: "zapti.me",
            is_default: false,
            team_id: props.eventType.team_id,
        },
        ...props.pageDomains,
    ];
});

// Fill the form before the auto-save watchers below are created, so opening
// the tab never saves on its own (and never detaches a domain it can't list).
initializeSenderForm();

function initializeSenderForm() {
    const domain = props.emailDomains.find(
        (domain) => domain.id === props.eventType.domain_id,
    );

    if (domain) {
        selectedDomain.value = domain;
    }

    if (props.eventType.email_username) {
        emailUsername.value = props.eventType.email_username;
    }

    if (props.eventType.sender_name) {
        senderName.value = props.eventType.sender_name;
    }

    if (props.eventType.reply_to) {
        replyTo.value = props.eventType.reply_to;
    }

    // The owner is represented by the default sender above, so list only the
    // other hosts here to avoid duplicating the owner.
    hostSenders.value = (props.eventType.users ?? [])
        .filter((pivot) => pivot.user_id !== props.eventType.user_id)
        .map((pivot) => {
            const teamUser = props.teamUsers.find(
                (u: any) => u.id === pivot.user_id,
            );

            return {
                userId: pivot.user_id,
                name: teamUser?.name ?? pivot.name ?? "",
                emailUsername: pivot.email_username ?? "",
                senderName: pivot.sender_name ?? "",
                replyTo: pivot.reply_to ?? "",
            };
        });
}

function selectDomain(domain: EmailDomain) {
    if (!domain.can_be_used_for_emails) {
        return;
    }

    if (selectedDomain.value && selectedDomain.value.id === domain.id) {
        selectedDomain.value = null;
        return;
    }

    selectedDomain.value = domain;
}

function selectPageDomain(pageDomain: PageDomainOption) {
    if (selectedPageDomainId.value === pageDomain.id) {
        return;
    }

    router.put(
        route("app.event-types.page-domain.update", {
            eventType: props.eventType.id,
        }),
        {
            page_domain_id: pageDomain.id,
        },
        {
            preserveState: true,
            preserveScroll: true,
        },
    );
}

watchThrottled(
    [selectedDomain, emailUsername, senderName, replyTo],
    ([domain, emailUsername]) => {
        if (
            domain &&
            emailUsername !== undefined &&
            domain.can_be_used_for_emails
        ) {
            assignDomain(domain.id);
        } else {
            detachDomain();
        }
    },
    { throttle: 500 },
);

watchThrottled(
    hostSenders,
    () => {
        if (!selectedDomain.value) {
            return;
        }

        saveHostSenders();
    },
    { throttle: 500, deep: true },
);

function saveHostSenders() {
    router.post(
        route("app.event-types.domains.senders", {
            eventType: props.eventType.id,
        }),
        {
            senders: hostSenders.value.map((host) => ({
                userId: host.userId,
                emailUsername: host.emailUsername,
                senderName: host.senderName,
                replyTo: host.replyTo,
            })),
        },
        {
            preserveState: true,
            preserveScroll: true,
        },
    );
}

function assignDomain(domainId: number) {
    router.post(
        route("app.event-types.domains.assign", {
            eventType: props.eventType.id,
        }),
        {
            domainId: domainId,
            emailUsername: emailUsername.value,
            senderName: senderName.value,
            replyTo: replyTo.value,
        },
        {
            preserveState: true,
            onSuccess: () => {
                emit("on-finish");
            },
        },
    );
}

function detachDomain() {
    router.post(
        route("app.event-types.domains.assign", {
            eventType: props.eventType.id,
        }),
        {
            domainId: null,
        },
        {
            preserveState: true,
            onSuccess: () => {
                emit("on-finish");
            },
        },
    );
}
</script>
